import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, Loader2, MessageCircle, Lock } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
type FormData = {
  name: string;
  phone: string;
  email: string;
  business: string;
  type: string;
  city: string;
  hasWebsite: string;
  message: string;
};
type Status = 'idle' | 'sending' | 'success' | 'error';
type Props = {
  /** Prefix for input ids so the form can appear twice on the page */
  idPrefix: string;
  /** Hide optional fields for a shorter form (used in the popup) */
  compact?: boolean;
  /** Called when the user wants to leave the success screen */
  onDone?: () => void;
};
const initialData: FormData = {
  name: '',
  phone: '',
  email: '',
  business: '',
  type: '',
  city: '',
  hasWebsite: '',
  message: ''
};
const FORM_ENDPOINT = 'https://formsubmit.co/ajax/aselitecontractor@gmail.com';
// Leads from this form go by text message (SMS) to +1 847-282-2098
const SMS_NUMBER = '+18472822098';
export function LeadForm({
  idPrefix,
  compact = false,
  onDone
}: Props) {
  const {
    t
  } = useLang();
  const c = t.contact;
  const [data, setData] = useState<FormData>(initialData);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [status, setStatus] = useState<Status>('idle');
  const businessTypes = [...t.socialProof.types, c.typeOther];
  const fid = (field: keyof FormData) => `${idPrefix}-${field}`;
  const update = (field: keyof FormData, value: string) => {
    setData(d => ({
      ...d,
      [field]: value
    }));
    if (errors[field]) setErrors(e => ({
      ...e,
      [field]: undefined
    }));
  };
  const validate = () => {
    const next: Partial<Record<keyof FormData, string>> = {};
    if (!data.name.trim()) next.name = c.required;
    if (!data.phone.trim()) next.phone = c.required;else if (data.phone.replace(/\D/g, '').length < 7) next.phone = c.invalidPhone;
    if (data.email.trim() && !/^\S+@\S+\.\S+$/.test(data.email)) next.email = c.invalidEmail;
    if (!data.business.trim()) next.business = c.required;
    if (!data.type) next.type = c.required;
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const smsUrl = () => {
    const lines = [c.waIntro, '', `${c.name}: ${data.name}`, `${c.phone}: ${data.phone}`, `${c.business}: ${data.business}`, `${c.type}: ${data.type}`, data.city ? `${c.city}: ${data.city}` : '', data.email ? `Email: ${data.email}` : '', data.hasWebsite ? `${c.hasWebsite} ${data.hasWebsite}` : '', data.message ? `${c.message}: ${data.message}` : ''].filter((line, i) => line !== '' || i === 1);
    // "?&body=" works on both iPhone and Android
    return `sms:${SMS_NUMBER}?&body=${encodeURIComponent(lines.join('\n'))}`;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    // Open the phone's Messages app with the lead details pre-written
    window.location.href = smsUrl();
    setStatus('sending');
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          _subject: `Nuevo lead: ${data.business} (${data.type})`,
          _template: 'table',
          _captcha: 'false',
          Nombre: data.name,
          Telefono: data.phone,
          Email: data.email || '—',
          Negocio: data.business,
          Tipo: data.type,
          Ciudad: data.city || '—',
          'Tiene website': data.hasWebsite || '—',
          Mensaje: data.message || '—',
          Origen: compact ? 'Popup' : 'Formulario de contacto'
        })
      });
      if (!res.ok) throw new Error('Request failed');
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };
  const inputBase = 'w-full bg-dark border rounded-xl px-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold transition-colors';
  const inputClass = (field: keyof FormData) => `${inputBase} ${errors[field] ? 'border-red-500/70' : 'border-white/10'}`;
  const labelClass = 'block text-sm font-medium text-gray-300 mb-2';
  const fieldError = (field: keyof FormData) => errors[field] ? <p id={`${fid(field)}-error`} className="mt-1.5 text-xs text-red-400" data-id="element-203" data-mp="1smgyipmbkzzu">
        {errors[field]}
      </p> : null;
  const a11y = (field: keyof FormData) => ({
    id: fid(field),
    'aria-invalid': !!errors[field],
    'aria-describedby': errors[field] ? `${fid(field)}-error` : undefined
  });
  return <AnimatePresence mode="wait" data-id="element-204" data-mp="1xno5wnkjvx5z">
      {status === 'success' ? <motion.div key="success" initial={{
      opacity: 0,
      scale: 0.96
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25,
      ease: [0.23, 1, 0.32, 1]
    }} className={`text-center ${compact ? 'py-6' : 'py-10'}`} role="status" data-id="element-205" data-mp="79dtxvbep655">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/15 text-gold mb-6" data-id="element-206" data-mp="3ta364nb25knv">
            <CheckCircle2 className="w-8 h-8" data-id="element-207" data-mp="3fjpy6ky625yj" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-3" data-id="element-208" data-mp="3ki15op0zo4dl">
            {c.successTitle}
          </h3>
          <p className="text-gray-400 max-w-md mx-auto mb-8" data-id="element-209" data-mp="35kqznv747qp9">{c.successText}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-210" data-mp="3al5658t2isl6">
            <a href={smsUrl()} className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gold hover:bg-gold-400 text-dark font-bold rounded-xl transition-colors" data-id="element-211" data-mp="2wl3vx2qwy8t1">
              <MessageCircle className="w-5 h-5" data-id="element-212" data-mp="ut7l5vckl2zb" />
              {c.successWhatsapp}
            </a>
            <button type="button" onClick={() => {
          setData(initialData);
          setStatus('idle');
          onDone?.();
        }} className="px-6 py-3 text-gray-400 hover:text-white font-medium rounded-xl border border-white/10 hover:border-white/20 transition-colors" data-id="element-213" data-mp="ztderh2egzk1">
              {onDone ? t.legal.close : c.sendAnother}
            </button>
          </div>
        </motion.div> : <motion.form key="form" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} onSubmit={handleSubmit} noValidate className={compact ? 'space-y-4' : 'space-y-5'} data-id="element-214" data-mp="1h1whsuleeozb">
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-5" data-id="element-215" data-mp="12drwrjyvi1dd">
            <div data-id="element-216" data-mp="12b5otj82ljum">
              <label htmlFor={fid('name')} className={labelClass} data-id="element-217" data-mp="1zsnn9l29lv8f">
                {c.name} <span className="text-gold" data-id="element-218" data-mp="1zuqo704yh8rx">*</span>
              </label>
              <input {...a11y('name')} type="text" autoComplete="name" value={data.name} onChange={e => update('name', e.target.value)} placeholder={c.namePh} className={inputClass('name')} data-id="element-219" data-mp="1v15o92jsy4q3" />
              {fieldError('name')}
            </div>
            <div data-id="element-220" data-mp="2hamghcm89ogd">
              <label htmlFor={fid('phone')} className={labelClass} data-id="element-221" data-mp="2hb497z9olkjj">
                {c.phone} <span className="text-gold" data-id="element-222" data-mp="2lcpt44kmuz38">*</span>
              </label>
              <input {...a11y('phone')} type="tel" autoComplete="tel" value={data.phone} onChange={e => update('phone', e.target.value)} placeholder={c.phonePh} className={inputClass('phone')} data-id="element-223" data-mp="2qb11eava51jc" />
              {fieldError('phone')}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-5" data-id="element-224" data-mp="34y436tqmxwch">
            <div data-id="element-225" data-mp="3921ysugr4p0s">
              <label htmlFor={fid('business')} className={labelClass} data-id="element-226" data-mp="38zftl1hc2llq">
                {c.business} <span className="text-gold" data-id="element-227" data-mp="3uaw89t6nvo8u">*</span>
              </label>
              <input {...a11y('business')} type="text" autoComplete="organization" value={data.business} onChange={e => update('business', e.target.value)} placeholder={c.businessPh} className={inputClass('business')} data-id="element-228" data-mp="3q9u5519mis1l" />
              {fieldError('business')}
            </div>
            <div data-id="element-229" data-mp="36nxvb5hr2yfo">
              <label htmlFor={fid('type')} className={labelClass} data-id="element-230" data-mp="36oh867sjmwmu">
                {c.type} <span className="text-gold" data-id="element-231" data-mp="3lcu7nequaxm3">*</span>
              </label>
              <select {...a11y('type')} value={data.type} onChange={e => update('type', e.target.value)} className={`${inputClass('type')} ${data.type ? '' : 'text-gray-600'}`} data-id="element-232" data-mp="3gkkbvi31n3bw">
                <option value="" disabled data-id="element-233" data-mp="2nuap6ixnipef">
                  {c.typePh}
                </option>
                {businessTypes.map(type => <option key={type} value={type} className="text-white" data-id="element-234" data-mp="185m67j14varn">
                    {type}
                  </option>)}
              </select>
              {fieldError('type')}
            </div>
          </div>

          {!compact && <>
              <div className="grid sm:grid-cols-2 gap-5" data-id="element-235" data-mp="1m4u8wlwi910l">
                <div data-id="element-236" data-mp="1m5dlro7asz7r">
                  <label htmlFor={fid('city')} className={labelClass} data-id="element-237" data-mp="1m313f9xmomu6">
                    {c.city}
                  </label>
                  <input {...a11y('city')} type="text" autoComplete="address-level2" value={data.city} onChange={e => update('city', e.target.value)} placeholder={c.cityPh} className={inputClass('city')} data-id="element-238" data-mp="1d4p3ghavgcp2" />
                </div>
                <div data-id="element-239" data-mp="1kuiv1pjwzsi2">
                  <label htmlFor={fid('email')} className={labelClass} data-id="element-240" data-mp="1kuadhkcujz0j">
                    {c.email}
                  </label>
                  <input {...a11y('email')} type="email" autoComplete="email" value={data.email} onChange={e => update('email', e.target.value)} placeholder={c.emailPh} className={inputClass('email')} data-id="element-241" data-mp="1ovm700yud4e0" />
                  {fieldError('email')}
                </div>
              </div>

              <fieldset data-id="element-242" data-mp="s5v3jc8ko6w2">
                <legend className={labelClass} data-id="element-243" data-mp="s6myckuci0la">{c.hasWebsite}</legend>
                <div className="flex gap-3" data-id="element-244" data-mp="x4y9cywdmh53">
                  {[c.yes, c.no].map(option => <button key={option} type="button" onClick={() => update('hasWebsite', option)} aria-pressed={data.hasWebsite === option} className={`flex-1 sm:flex-none sm:w-28 py-2.5 rounded-xl border font-medium transition-colors ${data.hasWebsite === option ? 'bg-gold text-dark border-gold' : 'bg-dark text-gray-300 border-white/10 hover:border-white/25'}`} data-id="element-245" data-mp="2mjw9si6gbw66">
                      {option}
                    </button>)}
                </div>
              </fieldset>

              <div data-id="element-246" data-mp="3ff3v420ssjj3">
                <label htmlFor={fid('message')} className={labelClass} data-id="element-247" data-mp="3fdize92qyujn">
                  {c.message}
                </label>
                <textarea {...a11y('message')} rows={4} value={data.message} onChange={e => update('message', e.target.value)} placeholder={c.messagePh} className={`${inputClass('message')} resize-none`} data-id="element-248" data-mp="3tdaiupc19351" />
              </div>
            </>}

          {status === 'error' && <div role="alert" className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-4" data-id="element-249" data-mp="3u5lji2eu66j1">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" data-id="element-250" data-mp="7v7tpy68flxm" />
              <div className="text-sm" data-id="element-251" data-mp="cvdlrfgezag8">
                <p className="text-white font-bold" data-id="element-252" data-mp="cwewtjxfjwxy">{c.errorTitle}</p>
                <p className="text-gray-400" data-id="element-253" data-mp="gvnq6nok5ssz">
                  {c.errorText}{' '}
                  <a href={smsUrl()} className="text-gold font-bold hover:underline" data-id="element-254" data-mp="lv0d18ffizvk">
                    847-282-2098 →
                  </a>
                </p>
              </div>
            </div>}

          <button type="submit" disabled={status === 'sending'} className="w-full inline-flex items-center justify-center gap-2 py-4 bg-gold hover:bg-gold-400 disabled:opacity-70 disabled:cursor-not-allowed text-dark font-bold rounded-xl transition-colors" data-id="element-255" data-mp="10koi8kqqeiok">
            {status === 'sending' ? <>
                <Loader2 className="w-5 h-5 animate-spin" data-id="element-256" data-mp="2l1fi0dv9xhk9" />
                {c.sending}
              </> : c.submit}
          </button>

          <p className="flex items-center justify-center gap-2 text-xs text-gray-500" data-id="element-257" data-mp="2b5kryxoq0k7g">
            <Lock className="w-3.5 h-3.5" data-id="element-258" data-mp="38sky8u54mbv3" />
            {c.privacy}
          </p>
        </motion.form>}
    </AnimatePresence>;
}